/**
 * TradeOS Analytics — PostHog wrapper
 *
 * All PostHog calls are funnelled through this module so:
 *   - The rest of the codebase never imports posthog-js directly
 *   - Initialisation (self-hosted vs disabled) happens in one place
 *   - A single `VITE_POSTHOG_DISABLED=true` env var kills tracking everywhere
 *
 * Self-hosted: set VITE_POSTHOG_HOST=http://localhost:18020 in .env
 * Cloud:       set VITE_POSTHOG_HOST=https://app.posthog.com
 * Disabled:    set VITE_POSTHOG_DISABLED=true (or leave VITE_POSTHOG_KEY empty)
 */

import posthog from 'posthog-js';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const POSTHOG_HOST = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ?? 'http://localhost:18020';
const POSTHOG_DISABLED = import.meta.env.VITE_POSTHOG_DISABLED === 'true';

let initialised = false;

/** Call once at app boot (main.tsx). Idempotent. */
export function initAnalytics(): void {
  if (initialised || POSTHOG_DISABLED || !POSTHOG_KEY) return;

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    // Don't automatically track page views — we do it manually per route
    // so we can attach extra properties (route name, auth state).
    capture_pageview: false,
    // Respect user's Do-Not-Track browser preference
    respect_dnt: true,
    // Disable session recording by default; opt-in per environment
    disable_session_recording: true,
    // Reduce noise in dev
    loaded: (ph) => {
      if (import.meta.env.DEV) {
        ph.debug(false);
      }
    },
  });

  initialised = true;
}

/** Returns true when PostHog is live and usable. */
function isLive(): boolean {
  return initialised && !POSTHOG_DISABLED && !!POSTHOG_KEY;
}

// ---------------------------------------------------------------------------
// Identity
// ---------------------------------------------------------------------------

/**
 * Identify a signed-in user. Call after a successful login or sign-up.
 * `accountId` is the distinct_id; extra traits are stored on the person record.
 */
export function identifyUser(
  accountId: string,
  traits?: {
    email?: string;
    role?: string;
    createdAt?: string;
    interests?: Record<string, unknown>;
  },
): void {
  if (!isLive()) return;
  posthog.identify(accountId, {
    email: traits?.email,
    role: traits?.role,
    created_at: traits?.createdAt,
    ...traits?.interests,
  });
}

/** Reset identity on sign-out. */
export function resetIdentity(): void {
  if (!isLive()) return;
  posthog.reset();
}

// ---------------------------------------------------------------------------
// Page views
// ---------------------------------------------------------------------------

/** Track a page view manually (called by the router on navigation). */
export function trackPageView(path: string, properties?: Record<string, unknown>): void {
  if (!isLive()) return;
  posthog.capture('$pageview', { $current_url: path, ...properties });
}

// ---------------------------------------------------------------------------
// Landing page events
// ---------------------------------------------------------------------------

export function trackCTAClick(label: string, destination: string): void {
  if (!isLive()) return;
  posthog.capture('cta_clicked', { label, destination });
}

export function trackProfileSelected(profile: string): void {
  if (!isLive()) return;
  posthog.capture('landing_profile_selected', { profile });
}

export function trackSectionViewed(section: string): void {
  if (!isLive()) return;
  posthog.capture('landing_section_viewed', { section });
}

// ---------------------------------------------------------------------------
// Auth events
// ---------------------------------------------------------------------------

export function trackSignInAttempt(): void {
  if (!isLive()) return;
  posthog.capture('sign_in_attempted');
}

export function trackSignInSuccess(accountId: string, email: string, role: string): void {
  if (!isLive()) return;
  // Identify before capturing so the event is tied to the person
  posthog.identify(accountId, { email, role });
  posthog.capture('sign_in_succeeded', { role });
}

export function trackSignInFailure(reason: string): void {
  if (!isLive()) return;
  posthog.capture('sign_in_failed', { reason });
}

export function trackSignUpStepCompleted(step: 'account' | 'interests'): void {
  if (!isLive()) return;
  posthog.capture('sign_up_step_completed', { step });
}

export function trackSignUpCompleted(
  accountId: string,
  email: string,
  interests: Record<string, unknown>,
): void {
  if (!isLive()) return;
  posthog.identify(accountId, { email, ...interests });
  posthog.capture('sign_up_completed', { ...interests });
}

// ---------------------------------------------------------------------------
// Onboarding events
// ---------------------------------------------------------------------------

export function trackOnboardingProfileSelected(profile: string): void {
  if (!isLive()) return;
  posthog.capture('onboarding_profile_selected', { profile });
}

// ---------------------------------------------------------------------------
// Feature events (authenticated app)
// ---------------------------------------------------------------------------

export function trackFeature(event: string, properties?: Record<string, unknown>): void {
  if (!isLive()) return;
  posthog.capture(event, properties);
}

// ---------------------------------------------------------------------------
// Raw escape hatch — use sparingly
// ---------------------------------------------------------------------------
export { posthog };
